import { useCallback } from 'react';
import { GROUP_BY_STATUS } from '@atlassian/jira-business-constants/src/index.tsx';
import type { JiraProject } from '@atlassian/jira-business-entity-common/src/types.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { standardIssueTypes } from '@atlassian/jira-common-constants/src/issue-types.tsx';
import { statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import {
	DRAG_DROP_UNKNOWN_FIELD_ID,
	FIELD_TYPE_MAP,
} from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { RANK_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { BoardIssue, Group, Rank, IssueTransition } from '../../../common/types.tsx';
import { ASSIGNEE_ID, ISSUE_TYPE_ID, NULL_GROUP_KEY, OTHER } from '../../../common/constants.tsx';

type CardDropAnalyticsProps = {
	issue: BoardIssue;
	sourceGroup: Group;
	destinationGroup: Group;
	rank: Rank | undefined;
	transition: IssueTransition | null | undefined;
};

type AnalyticsAttributesProps = CardDropAnalyticsProps & {
	project: JiraProject;
};

export const useCardDragDropAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const project = useProject();

	const fireStartCardDragAnalytics = useCallback(
		(issueId: number) => {
			getUpdateAnalyticsFlowHelper().fireAnalyticsStart(DRAG_DROP_UNKNOWN_FIELD_ID, {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: DRAG_DROP_UNKNOWN_FIELD_ID,
					isDragEditing: true,
					issueId,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const fireCardDropAnalytics = useCallback(
		({ issue, sourceGroup, destinationGroup, rank, transition }: CardDropAnalyticsProps) => {
			const attributes = getAnalyticsAttributes({
				issue,
				sourceGroup,
				destinationGroup,
				rank,
				transition,
				project,
			});
			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(DRAG_DROP_UNKNOWN_FIELD_ID, {
				analytics: createAnalyticsEvent({}),
				attributes,
			});
		},
		[createAnalyticsEvent, project],
	);

	return {
		fireStartCardDragAnalytics,
		fireCardDropAnalytics,
	};
};

const getAnalyticsAttributes = ({
	issue,
	sourceGroup,
	destinationGroup,
	rank,
	transition,
	project,
}: AnalyticsAttributesProps) => {
	const sourceGroupId = sourceGroup.id;
	const destinationGroupId = destinationGroup.id;
	const sourceGroupType = sourceGroup.type;
	const destinationGroupType = destinationGroup.type;
	const issueTypeName = issue.fields[ISSUE_TYPE_ID].issueType.name;

	const attributes: Record<string, string | number | boolean> = {
		assigneeId: issue.fields[ASSIGNEE_ID]?.user?.accountId ?? '',
		fieldType: destinationGroupType,
		fieldKey: destinationGroupType,
		oldValId: sourceGroupId !== NULL_GROUP_KEY ? sourceGroupId : '',
		newValId: destinationGroupId !== NULL_GROUP_KEY ? destinationGroupId : '',
		isClassic: !project.isSimplified,
		isDragEditing: true,
		isInlineEditing: false,
		hasRankChanged: rank != null,
		isProjectAdmin: project.permissions.administerProject,
		issueId: issue.id,
		issueType: standardIssueTypes[issueTypeName] ? issueTypeName : OTHER,
		projectId: project.id,
	};

	const isStatusUpdate =
		sourceGroupType === GROUP_BY_STATUS && destinationGroupType === GROUP_BY_STATUS;

	if (isStatusUpdate && transition) {
		const oldStatusCategoryId =
			// TODO: remove optional chaining on transition property during mbw-64_fetch_issue_transitions_on_card_hover cleanup
			transition.fromStatus && fg('mbw-43-allow-status-mapping-on-business-board')
				? transition.fromStatus.statusCategory.id
				: sourceGroup.statusCategoryId;
		const newStatusCategoryId =
			// TODO: remove optional chaining on transition property during mbw-64_fetch_issue_transitions_on_card_hover cleanup
			transition.toStatus && fg('mbw-43-allow-status-mapping-on-business-board')
				? transition.toStatus.statusCategory.id
				: destinationGroup.statusCategoryId;
		attributes.oldStatusCategoryId = oldStatusCategoryId;
		attributes.newStatusCategoryId = newStatusCategoryId;
		attributes.oldStatusCategoryName = statusCategoryForId(oldStatusCategoryId);
		attributes.newStatusCategoryName = statusCategoryForId(newStatusCategoryId);
	}

	if (sourceGroupId === destinationGroupId) {
		// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string | number | boolean'.
		attributes.fieldType = FIELD_TYPE_MAP[RANK_CF_TYPE];
		// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string | number | boolean'.
		attributes.fieldKey = FIELD_TYPE_MAP[RANK_CF_TYPE];
	}

	if (transition != null) {
		attributes.transitionId = transition.transitionId;
	}

	return attributes;
};
